import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs/internal/operators/finalize';
import { PageSize } from 'src/app/enumeration/pageSize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { SurveyStatus } from 'src/app/enumeration/surveyStatus';
import { SurveyServices } from 'src/app/services/survey-services';
import { CheckAccessRoles } from 'src/app/utils/check-access-roles';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';

@Component({
  selector: 'app-survey-list',
  standalone: true,
  imports: [
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgbModule,
    DateFormatPipe,
    CommonModule
  ],
  templateUrl: './survey-list.html',
  styleUrl: './survey-list.css',
})
export class SurveyList {

  private readonly fb = inject(FormBuilder);
  private readonly apiService = inject(SurveyServices);
  private readonly router = inject(Router);
  private readonly dateFormat = inject(DateFormatPipe);
  private readonly checkAccessRoles = inject(CheckAccessRoles);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  createSurveyForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    period: ['', [Validators.required, Validators.pattern('^[0-9]+$')]]
  });

  isUploading = signal<boolean>(false);
  loading = signal<boolean>(false);

  surveyList = signal<any[]>([])
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1);
  readonly pageSize = signal<number>(PageSize.SURVEY_LIST);

  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  isSuperAdminAndAdmin = signal<boolean>(false);

  statusMap: any = {
    OPEN: { label: SurveyStatus.OPEN, class: 'bg-success' },
    ACTIVE: { label: SurveyStatus.ACTIVE, class: 'bg-primary' },
    CLOSE: { label: SurveyStatus.CLOSE, class: 'bg-danger' }
  };

  ngOnInit(): void {
    this.isSuperAdminAndAdmin.set(this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN]))

    this.getAllSurveyList()
  }

  getAllSurveyList() {

    this.loading.set(true);

    const zeroBasedPage = this.page() - 1;

    this.apiService.getSurveyList(zeroBasedPage, this.pageSize())
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response: any) => {
          this.surveyList.set(response?.Object.surveyInfoLst || [])
          this.allTotal.set(response?.Object.allTotal);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching survey list.');
        }
      });

  }

  createSurvey(): void {

    if (this.createSurveyForm.invalid) {
      this.createSurveyForm.markAllAsTouched();
      return;
    }

    this.isUploading.set(true)
    var startDate = this.dateFormat.convertObjectToDDMMYYYY(this.createSurveyForm.get('startDate')?.value)
    var endDate = this.dateFormat.convertObjectToDDMMYYYY(this.createSurveyForm.get('endDate')?.value)


    this.apiService.getCreateSurvey(
      this.createSurveyForm.value.name,
      startDate,
      endDate,
      this.createSurveyForm.value.period)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isUploading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.showSuccess('Survey created successfully.');
          this.createSurveyForm.reset();
          this.getAllSurveyList();
          this.closeModalViaNativeElement('createSurveyModal')
        },
        error: (err: any) => {
          this.closeModalViaNativeElement('createSurveyModal')
          this.showError(err || 'Network error occurred while creating survey.');
        }
      });

  }

  activeSurvey(surveyId: any) {
    if (confirm('Are you sure you want to active this survey?')) {

      this.apiService.updateSurveyFormStatus(surveyId, SurveyStatus.ACTIVE)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (v: any) => {
            this.showSuccess('Survey activated successfully.');
            this.getAllSurveyList();
          },
          error: (err: any) => {
            this.showError(err || 'Network error occurred while activating survey.');
          }
        })
    }
  }

  closedSurvey(surveyId: any) {
    if (confirm('Are you sure you want to close this survey?')) {

      this.apiService.updateSurveyFormStatus(surveyId, SurveyStatus.CLOSE)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (v: any) => {
            this.showSuccess('Survey closed successfully.');
            this.getAllSurveyList();
          },
          error: (err: any) => {
            this.showError(err || 'Network error occurred while closing survey.');
          }
        })
    }
  }

  removeSurvey(surveyId: any) {
    if (confirm('Are you sure you want to remove this survey?')) {
      this.apiService.removeSurveyFormStatus(surveyId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (v: any) => {
            this.showSuccess('Survey removed successfully.');
            this.getAllSurveyList();
          },
          error: (err: any) => {
            this.showError(err || 'Network error occurred while removing survey.');
          }
        })
    }
  }

  viewDealerList(surveyId: any) {
    this.router.navigate(['/nav/dealerSurveyList', surveyId], { skipLocationChange: true });
  }

  goToSurveyInfo(surveyId: any) {
    this.router.navigate(['/nav/surveyInfo', surveyId, 'unknown'], { skipLocationChange: true });
  }

  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.getAllSurveyList();
    }
  }

  showError(msg: string): void {
    this.errorMessage.set(msg);
    setTimeout(() => this.clearAlerts(), 6000);
  }

  showSuccess(msg: string): void {
    this.successMessage.set(msg);
    setTimeout(() => this.clearAlerts(), 4000);
  }

  clearAlerts(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }

}
